import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ShowroomFilterTree, ShowroomFilterTreeValue } from 'Types/showroom';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Typography from '@material-ui/core/Typography';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

import { useStyles } from './styles';

export type ComponentProps = {
	tree: ShowroomFilterTree;
	selectedFilters: string[];
	maxInitialItems?: number;
	onFilterToggle(value: ShowroomFilterTreeValue): void;
};

const ShowroomContentFilterTree = (props: ComponentProps) => {
	const { tree, selectedFilters, maxInitialItems = 10, onFilterToggle } = props;

	const classes = useStyles();
	const { t } = useTranslation();

	const [open, setOpen] = React.useState(false);
	const [viewAll, setViewAll] = React.useState(false);

	const buttonClasses = React.useMemo(() => ({ root: classes.collapseIcon }), [classes.collapseIcon]);

	const onClickHandler: React.MouseEventHandler = React.useCallback(() => {
		setOpen(prev => !prev);
	}, []);
	const onShowMoreHandler: React.MouseEventHandler = React.useCallback(() => {
		setViewAll(prev => !prev);
	}, []);

	const renderChild = React.useCallback(
		(child: ShowroomFilterTreeValue, addDivider: boolean) => {
			let checkStatus: 'none' | 'checked' | 'affected';
			if (selectedFilters.includes(child.value.id)) checkStatus = 'checked';
			else if (
				some(selectedFilters, selectedFilter => !isEmpty(child.childs) && !isEmpty(selectedFilter.match(new RegExp(`^${child.value.id}`))))
			)
				checkStatus = 'affected';
			else checkStatus = 'none';

			return (
				<React.Fragment key={child.value.value}>
					<FormGroup row className={classes.filterValueBox}>
						<FormControlLabel
							control={
								<Checkbox
									id={`filterTreeCheckbox_${child.value.id}`}
									classes={{ root: classes.filterValueCheckboxRoot }}
									checked={checkStatus === 'checked' || checkStatus === 'affected'}
									indeterminate={checkStatus === 'affected'}
									onChange={_ => onFilterToggle(child)}
									color="primary"
									icon={<CheckBoxOutlineBlankIcon htmlColor="#DEDEDE" className={classes.filterValueCheckBoxIcon} />}
									indeterminateIcon={<IndeterminateCheckBoxIcon className={classes.filterValueCheckBoxIcon} />}
									checkedIcon={<CheckBoxIcon className={classes.filterValueCheckBoxIcon} />}
								/>
							}
							classes={{ root: classes.filterValueLabelRoot, label: classes.filterValueLabel }}
							label={
								<>
									<Typography
										component="span"
										className={clsx(classes.filterValueLabelText, {
											[classes.filterValueLabelParent]: !isEmpty(child.childs)
										})}
									>
										{child.value.value}
									</Typography>
									<Typography component="span" className={clsx(classes.filterValueLabelText, classes.filterValueLabelCounter)}>
										{child.value.mediaIds.length}
									</Typography>
								</>
							}
						/>
					</FormGroup>
					{addDivider && <Divider className={classes.childDivider} />}
				</React.Fragment>
			);
		},
		[classes, onFilterToggle, selectedFilters]
	);

	const [firstChilds, showMoreChilds, totalTreeItems] = React.useMemo(() => {
		let renderedElements = [];
		const renderElements = (childs: ShowroomFilterTreeValue[], lastParent = false) => {
			childs.forEach((child, index, arr) => {
				const lastChild = index === arr.length - 1;
				renderedElements.push(renderChild(child, child.parent !== null && lastChild && !lastParent));
				renderElements(child.childs, lastChild);
			});
		};
		renderElements(tree.childs);
		const firstChilds = renderedElements.slice(0, maxInitialItems);
		const showMoreChilds = renderedElements.slice(maxInitialItems);
		return [firstChilds, showMoreChilds, renderedElements.length];
	}, [maxInitialItems, renderChild, tree.childs]);

	return (
		<div className={classes.filterTreeContainer}>
			<div id={`${tree.name}_header`} className={classes.filterHeader} onClick={onClickHandler}>
				<Typography component="h5" className={classes.filterTitle}>
					{tree.name.replace('samples_', '').replace('sampleType', 'type')}
				</Typography>
				{open ? <KeyboardArrowUpIcon classes={buttonClasses} /> : <KeyboardArrowDownIcon classes={buttonClasses} />}
			</div>
			<Collapse in={open}>
				<div id={`${tree.name}_childsContainer`} className={classes.filterValuesContainer}>
					{firstChilds}
					{!isEmpty(showMoreChilds) && <Collapse in={viewAll}>{showMoreChilds}</Collapse>}
				</div>
				{totalTreeItems > maxInitialItems && (
					<Button
						id={`${tree.name}_showButton`}
						classes={{ root: classes.showMoreButton, label: classes.showMoreButtonLabel }}
						disableRipple
						disableElevation
						fullWidth
						onClick={onShowMoreHandler}
						startIcon={viewAll ? <KeyboardArrowUpIcon classes={buttonClasses} /> : <KeyboardArrowDownIcon classes={buttonClasses} />}
					>
						{viewAll ? t('showroom.sectionFilters.showLess') : t('showroom.sectionFilters.showMore')}
					</Button>
				)}
			</Collapse>
		</div>
	);
};

export default React.memo(ShowroomContentFilterTree);
